import { useEffect, useMemo, useState, useCallback } from 'react';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import FormattedMessage from 'I18n/components/FormattedMessage';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { View } from 'foundations-components/transitional/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { ACADEMY_PAGE_MAX_WIDTH, useMediaQuery } from 'academy-common-lib/utils/Styling';
import { getOverviewModalParams } from 'academy-common-lib/utils/url';
import getLang from 'academy-common-lib/utils/getLang';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { SearchScoringBaseConfiguration, useSearch } from 'academy-common-lib/features/search/hooks/useSearch';
import Carousel from '../Carousel';
import { useHistory, useLocation } from 'react-router-dom';
import MostPopularCertificatesHeader from './MostPopularCertificatesHeader';
import CertificateButton from './CertificateButton/CertificateButton';
import UsageTracker from '../../utils/UsageTracker';
import { UserTrackStatuses } from '../../constants/TracksConstants';
import { safeSmoothScrollTo } from '../../utils/utils';
import ExpandedCertificate from './ExpandedCertificate';
import { useSearchFeedback } from '../../features/search/searchQueries';
import { useMyLearning } from '../../features/myLearning/myLearningQueries';
import { getSearchUrl } from '../../features/search/searchRouting';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const useMostPopularCertificates = () => {
  const language = getLang();

  // Get completed certificates count
  const {
    myLearningData
  } = useMyLearning({
    variables: {
      contentTypes: ['TRACK'],
      progressType: 'COMPLETED',
      hasCertification: true,
      offset: {
        cursor: 0
      }
    }
  });
  const numCompletedCertificates = myLearningData === null || myLearningData === void 0 ? void 0 : myLearningData.total;

  // Get certificate IDs based on language
  const {
    searchResults,
    searchResultsLoading,
    correlationId
  } = useSearch({
    variables: {
      contentTypes: ['TRACK'],
      hasAwardsOnly: true,
      languages: [language.toUpperCase()],
      pageLength: 12,
      offset: {
        cursor: 0
      },
      shouldFilterDeprecated: true,
      featuredHomepageOnly: true,
      featuredCertificationsOnly: true
    },
    scoringConfig: Object.assign({}, SearchScoringBaseConfiguration, {
      includeRecencyDecay: false
    })
  });
  const certificateIds = (searchResults === null || searchResults === void 0 ? void 0 : searchResults.map(result => result.id)) || [];

  // Get card models for certificates
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError
  } = useLearningContentCardModels({
    items: certificateIds.map(id => {
      var _searchResults$find;
      return {
        id,
        type: 'TRACK',
        language: language,
        resultMLOptionId: searchResults === null || searchResults === void 0 || (_searchResults$find = searchResults.find(result => result.id === id)) === null || _searchResults$find === void 0 ? void 0 : _searchResults$find.resultMLOptionId
      };
    }),
    skip: !certificateIds.length
  });
  return {
    certificates: cardModels || [],
    certificatesLoading: cardModelsLoading || searchResultsLoading,
    certificatesError: cardModelsError,
    numCompletedCertificates,
    correlationId,
    isEnglish: language === 'en'
  };
};
const MostPopularCertificates = () => {
  var _selectedCardModel$pr;
  const {
    isMobile
  } = useMediaQuery();
  const history = useHistory();
  const location = useLocation();
  const {
    certificates,
    numCompletedCertificates,
    correlationId,
    isEnglish
  } = useMostPopularCertificates();
  const [sendSearchFeedback] = useSearchFeedback();
  const [selectedCertificateId, setSelectedCertificateId] = useState();
  const selectedCardModel = useMemo(() => certificates.find(({
    id
  }) => id === selectedCertificateId), [certificates, selectedCertificateId]);
  const status = selectedCardModel === null || selectedCardModel === void 0 || (_selectedCardModel$pr = selectedCardModel.progress) === null || _selectedCardModel$pr === void 0 ? void 0 : _selectedCardModel$pr.status;
  useEffect(() => {
    if (certificates !== null && certificates !== void 0 && certificates[0] && !selectedCertificateId) {
      const index = certificates.findIndex(element => !element.progress || element.progress.status !== 'COMPLETED');
      setSelectedCertificateId(certificates[index > 0 ? index : 0].id);
    }
  }, [certificates, selectedCertificateId]);
  const handleExpandedCertificateClick = useCallback(() => {
    // Only send feedback for non-English users since their results come from search
    if (!isEnglish && correlationId && selectedCardModel && selectedCardModel.resultMLOptionId) {
      sendSearchFeedback({
        variables: {
          correlationId,
          contentContainerType: 'TRACK',
          resultMLOptionId: selectedCardModel.resultMLOptionId
        }
      }).catch(Raven.captureException.bind(Raven));
    }

    // Existing tracking
    UsageTracker.track('interaction', {
      action: 'content-card-click',
      contentType: 'TRACK',
      trackId: selectedCertificateId,
      appearsIn: 'popular-certificates',
      position: certificates.findIndex(card => card.id === selectedCertificateId) + 1,
      name: selectedCardModel === null || selectedCardModel === void 0 ? void 0 : selectedCardModel.name,
      style: 'expanded',
      isInProgress: status === 'IN_PROGRESS',
      isCertification: true
    });
  }, [isEnglish, correlationId, selectedCardModel, selectedCertificateId, certificates, status, sendSearchFeedback]);
  return certificates.length > 0 ? /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-surface-recessed",
    inlineSize: "100%",
    marginBlockEnd: "0",
    overflow: "hidden",
    children: /*#__PURE__*/_jsxs(View, {
      inlineSize: "100%",
      marginBlock: "0",
      marginInline: "auto",
      maxInlineSize: `${ACADEMY_PAGE_MAX_WIDTH}px`,
      paddingBlockEnd: isMobile ? '400' : '1300',
      paddingBlockStart: isMobile ? '600' : '1300',
      paddingInline: isMobile ? '400' : '1100',
      children: [/*#__PURE__*/_jsx(MostPopularCertificatesHeader, {
        numCompletedCertificates: numCompletedCertificates || 0
      }), /*#__PURE__*/_jsx(Carousel, {
        carouselProps: {
          paddingBlockEnd: '0'
        },
        children: [...certificates.slice(0, isMobile ? 4 : certificates.length).map(cardModel => {
          var _progress, _cardModel$progress;
          const award = cardModel === null || cardModel === void 0 || (_progress = cardModel.progress) === null || _progress === void 0 ? void 0 : _progress.award;
          return /*#__PURE__*/_jsx(CertificateButton, {
            expiresAt: (award === null || award === void 0 ? void 0 : award.expiresAt) || 0,
            isCompleted: ((_cardModel$progress = cardModel.progress) === null || _cardModel$progress === void 0 ? void 0 : _cardModel$progress.status) === 'COMPLETED',
            isExpired: (award === null || award === void 0 ? void 0 : award.isExpired) || false,
            isSelected: selectedCertificateId === cardModel.id,
            name: cardModel.name,
            onClick: () => {
              UsageTracker.track('interaction', {
                action: 'most-popular-certificates-track-clicked',
                id: `${cardModel.id}`
              });
              setSelectedCertificateId(cardModel.id);
            },
            trackId: cardModel.id
          }, `certificate_button_${cardModel.id}`);
        }), /*#__PURE__*/_jsx(Button, {
          onClick: e => {
            e.stopPropagation();
            UsageTracker.track('interaction', {
              action: 'most-popular-certificates-view-all-clicked'
            });
            history.push(getSearchUrl({
              showOnlyCerts: true
            }));
            safeSmoothScrollTo(window, 0, 0);
          },
          padding: "0",
          use: "secondary",
          children: /*#__PURE__*/_jsx(View, {
            minInlineSize: "248px",
            padding: "400",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.mostPopularCertificates.viewAllCertificates"
            })
          })
        }, "view-all")]
      }), /*#__PURE__*/_jsx(AccordionTransition, {
        open: !!selectedCertificateId,
        renderContent: () => /*#__PURE__*/_jsx(RouterLink, {
          onClick: handleExpandedCertificateClick,
          to: status && status !== UserTrackStatuses.NOT_STARTED ? `/tracks/${selectedCertificateId}` : `${location.pathname}?${new URLSearchParams(getOverviewModalParams({
            id: selectedCertificateId,
            type: 'TRACK',
            language: getLang(),
            source: 'certificates'
          }))}`,
          use: "unstyled",
          children: selectedCardModel && /*#__PURE__*/_jsx(ExpandedCertificate, {
            cardModel: selectedCardModel
          })
        })
      })]
    })
  }) : null;
};
export default MostPopularCertificates;